import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiBookOpen, FiClock, FiCheckCircle, FiAlertCircle,
  FiFilter, FiSearch, FiBook, FiCalendar, FiRefreshCw, FiX
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Peminjaman.css';

const Peminjaman = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [borrowings, setBorrowings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cancelling, setCancelling] = useState(null);

  const fetchBorrowings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getMyBorrowings(user?.id);
      if (response.success) {
        setBorrowings(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching borrowings:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      fetchBorrowings();
    }
  }, [user, fetchBorrowings]);

  const getStatusInfo = (item) => {
    // Use status_display if available (includes is_approved logic from API)
    const status = item.status_display || item.status;
    const tanggalKembali = item.tanggal_kembali;
    const isApproved = item.is_approved !== undefined ? parseInt(item.is_approved) : 1;
    
    // Check is_approved first
    if (isApproved === 0) {
      return { label: 'Menunggu Konfirmasi', className: 'pending', icon: FiClock };
    }
    if (isApproved === -1) {
      return { label: 'Ditolak', className: 'rejected', icon: FiAlertCircle };
    }
    
    const today = new Date();
    const dueDate = new Date(tanggalKembali);
    const isOverdue = status === 'dipinjam' && today > dueDate;

    if (status === 'dikembalikan') {
      return { label: 'Dikembalikan', className: 'returned', icon: FiCheckCircle };
    }
    if (status === 'menunggu') {
      return { label: 'Menunggu Konfirmasi', className: 'pending', icon: FiClock };
    }
    if (status === 'ditolak') {
      return { label: 'Ditolak', className: 'rejected', icon: FiAlertCircle };
    }
    if (isOverdue) {
      return { label: 'Terlambat', className: 'overdue', icon: FiAlertCircle };
    }
    if (status === 'dipinjam') {
      return { label: 'Dipinjam', className: 'active', icon: FiBookOpen };
    }
    return { label: status, className: 'pending', icon: FiClock };
  };

  const getDaysRemaining = (tanggalKembali) => {
    const today = new Date();
    const dueDate = new Date(tanggalKembali);
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Helper to get effective status (considering is_approved)
  const getEffectiveStatus = (item) => {
    const isApproved = item.is_approved !== undefined ? parseInt(item.is_approved) : 1;
    if (isApproved === 0) return 'menunggu';
    if (isApproved === -1) return 'ditolak';
    return item.status_display || item.status;
  };

  const filteredBorrowings = borrowings.filter(item => {
    const effectiveStatus = getEffectiveStatus(item);
    
    // Filter by status
    if (filter === 'active' && effectiveStatus !== 'dipinjam') return false;
    if (filter === 'returned' && effectiveStatus !== 'dikembalikan') return false;
    if (filter === 'pending' && effectiveStatus !== 'menunggu') return false;
    if (filter === 'overdue') {
      const today = new Date();
      const dueDate = new Date(item.tanggal_kembali);
      if (effectiveStatus !== 'dipinjam' || today <= dueDate) return false;
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return item.judul?.toLowerCase().includes(query) ||
             item.pengarang?.toLowerCase().includes(query);
    }

    return true;
  });

  const stats = {
    total: borrowings.length,
    pending: borrowings.filter(b => getEffectiveStatus(b) === 'menunggu').length,
    active: borrowings.filter(b => {
      const status = getEffectiveStatus(b);
      return status === 'dipinjam';
    }).length,
    returned: borrowings.filter(b => getEffectiveStatus(b) === 'dikembalikan').length,
    overdue: borrowings.filter(b => {
      const status = getEffectiveStatus(b);
      const today = new Date();
      const dueDate = new Date(b.tanggal_kembali);
      return status === 'dipinjam' && today > dueDate;
    }).length
  };

  // Handle cancel borrowing
  const handleCancelBorrow = async (item) => {
    if (!window.confirm(`Batalkan peminjaman buku "${item.judul}"?`)) {
      return;
    }
    
    setCancelling(item.id);
    try {
      const response = await api.cancelBorrow(item.id, user?.id);
      if (response.success) {
        // Refresh borrowings list
        fetchBorrowings();
      } else {
        alert(response.message || 'Gagal membatalkan peminjaman');
      }
    } catch (error) {
      console.error('Error cancelling borrow:', error);
      alert('Terjadi kesalahan saat membatalkan peminjaman');
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="peminjaman">
      {/* Header */}
      <header className="peminjaman__header">
        <div>
          <h1 className="peminjaman__title">Peminjaman Saya</h1>
          <p className="peminjaman__subtitle">Kelola dan pantau status peminjaman buku Anda</p>
        </div>
        <button className="peminjaman__refresh" onClick={fetchBorrowings}>
          <FiRefreshCw /> Refresh
        </button>
      </header>

      {/* Stats */}
      <div className="peminjaman__stats">
        <div 
          className={`peminjaman__stat ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          <span className="peminjaman__stat-value">{stats.total}</span>
          <span className="peminjaman__stat-label">Total Pinjaman</span>
        </div>
        <div 
          className={`peminjaman__stat peminjaman__stat--pending ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          <span className="peminjaman__stat-value">{stats.pending}</span>
          <span className="peminjaman__stat-label">Menunggu</span>
        </div>
        <div 
          className={`peminjaman__stat peminjaman__stat--active ${filter === 'active' ? 'active' : ''}`}
          onClick={() => setFilter('active')}
        >
          <span className="peminjaman__stat-value">{stats.active}</span>
          <span className="peminjaman__stat-label">Dipinjam</span>
        </div>
        <div 
          className={`peminjaman__stat peminjaman__stat--overdue ${filter === 'overdue' ? 'active' : ''}`}
          onClick={() => setFilter('overdue')}
        >
          <span className="peminjaman__stat-value">{stats.overdue}</span>
          <span className="peminjaman__stat-label">Terlambat</span>
        </div>
        <div 
          className={`peminjaman__stat peminjaman__stat--returned ${filter === 'returned' ? 'active' : ''}`}
          onClick={() => setFilter('returned')}
        >
          <span className="peminjaman__stat-value">{stats.returned}</span>
          <span className="peminjaman__stat-label">Dikembalikan</span>
        </div>
      </div>

      {/* Filters */}
      <div className="peminjaman__filters">
        <div className="peminjaman__search">
          <FiSearch />
          <input
            type="text"
            placeholder="Cari judul buku..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="peminjaman__filter-tabs">
          <button 
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            Semua
          </button>
          <button 
            className={filter === 'pending' ? 'active' : ''}
            onClick={() => setFilter('pending')}
          >
            Menunggu
          </button>
          <button 
            className={filter === 'active' ? 'active' : ''}
            onClick={() => setFilter('active')}
          >
            Aktif
          </button>
          <button 
            className={filter === 'overdue' ? 'active' : ''}
            onClick={() => setFilter('overdue')}
          >
            Terlambat
          </button>
          <button 
            className={filter === 'returned' ? 'active' : ''}
            onClick={() => setFilter('returned')}
          >
            Selesai
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="peminjaman__loading">
          <div className="peminjaman__loading-spinner"></div>
          <p>Memuat data peminjaman...</p>
        </div>
      ) : filteredBorrowings.length === 0 ? (
        <div className="peminjaman__empty">
          <FiBookOpen className="peminjaman__empty-icon" />
          <h3>Tidak ada data peminjaman</h3>
          <p>
            {filter !== 'all' 
              ? 'Tidak ada peminjaman dengan filter ini'
              : 'Anda belum memiliki riwayat peminjaman buku'}
          </p>
          {filter !== 'all' && (
            <button onClick={() => setFilter('all')}>Lihat Semua</button>
          )}
          <Link to="/katalog" className="peminjaman__empty-link">
            Jelajahi Katalog
          </Link>
        </div>
      ) : (
        <div className="peminjaman__list">
          {filteredBorrowings.map(item => {
            const statusInfo = getStatusInfo(item);
            const daysRemaining = getDaysRemaining(item.tanggal_kembali);
            const StatusIcon = statusInfo.icon;

            return (
              <div key={item.id} className="peminjaman__item">
              <div className="peminjaman__item-cover">
                  <img 
                    src={item.cover_image || '/images/cover-default.png'} 
                    alt={item.judul}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/images/cover-default.png';
                    }}
                  />
                </div>

                <div className="peminjaman__item-content">
                  <div className="peminjaman__item-header">
                    <Link to={`/katalog/${item.id_buku}`} className="peminjaman__item-title">
                      {item.judul}
                    </Link>
                    <span className={`peminjaman__item-status peminjaman__item-status--${statusInfo.className}`}>
                      <StatusIcon />
                      {statusInfo.label}
                    </span>
                  </div>

                  <p className="peminjaman__item-author">{item.penulis || 'Unknown'}</p>

                  <div className="peminjaman__item-dates">
                    <div className="peminjaman__item-date">
                      <FiCalendar />
                      <span>
                        Tanggal Pinjam: {new Date(item.tanggal_pinjam).toLocaleDateString('id-ID', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="peminjaman__item-date">
                      <FiClock />
                      <span>
                        Batas Kembali: {new Date(item.tanggal_kembali).toLocaleDateString('id-ID', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>

                  {getEffectiveStatus(item) === 'dipinjam' && (
                    <div className={`peminjaman__item-remaining ${daysRemaining < 0 ? 'overdue' : daysRemaining <= 3 ? 'warning' : ''}`}>
                      {daysRemaining < 0 ? (
                        <span>Terlambat {Math.abs(daysRemaining)} hari</span>
                      ) : daysRemaining === 0 ? (
                        <span>Jatuh tempo hari ini!</span>
                      ) : (
                        <span>{daysRemaining} hari lagi</span>
                      )}
                    </div>
                  )}

                  {/* Cancel button for pending borrowings */}
                  {getEffectiveStatus(item) === 'menunggu' && (
                    <button 
                      className="peminjaman__item-cancel"
                      onClick={() => handleCancelBorrow(item)}
                      disabled={cancelling === item.id}
                    >
                      <FiX />
                      {cancelling === item.id ? 'Membatalkan...' : 'Batalkan Peminjaman'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Peminjaman;
